import {
  useState,
  useEffect,
  ChangeEvent,
  FC,
  useCallback,
  SyntheticEvent,
  useMemo,
  ReactNode,
  HTMLAttributes,
} from 'react';
import debounce from 'lodash/debounce';
import { createUseStyles } from 'react-jss';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { FormProps } from '../form/helpers';
import { UiOption } from '@/lib/helpers';
import FormControl from '../form/form-control';
import { Box } from '@mui/material';
import { sortBy } from 'lodash';

interface Props extends FormProps {
  value: UiOption[];
  onChange: (value: UiOption[], event: ChangeEvent<HTMLInputElement>) => void;
  onSearch: (value: string) => Promise<UiOption[]>;
  onOptionRender?: (props: HTMLAttributes<HTMLLIElement>, option: UiOption) => ReactNode;
  options?: UiOption[];
}

const useStyles = createUseStyles({
  autocomplete: {
    '& .MuiAutocomplete-inputRoot': {
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      padding: '6px !important',
      maxWidth: 500,
    },
    '& .MuiAutocomplete-tag': {
      maxWidth: '100%',
    },
    '& .MuiAutocomplete-input': {
      minWidth: 300,
      flexGrow: 1,
    },
  },
  autocompleteWithSelections: {
    '& .MuiAutocomplete-inputRoot': {
      rowGap: 6,
    },
    '& .MuiAutocomplete-input': {
      flexBasis: '100%',
      width: '100% !important',
      minWidth: '100%',
    },
  },
});

const SelectSearchMulti: FC<Props> = ({
  onSearch,
  onChange,
  label,
  error = '',
  name,
  value,
  size = 'medium',
  onOptionRender,
  options: propOptions = [],
}) => {
  const styles = useStyles();
  const sortedPropOptions = useMemo(
    () =>
      sortBy(propOptions, (option) =>
        String(option.labelSimple || option.label || '').toLowerCase()
      ),
    [propOptions]
  );
  const [options, setOptions] = useState<UiOption[]>(sortedPropOptions);
  const [keyword, setKeyword] = useState('');

  const handleChange = (event: ChangeEvent<HTMLInputElement>, newValue: UiOption[]) => {
    if (newValue.length > value.length) {
      setKeyword('');
    }
    onChange(newValue, event);
  };

  const handleInputChange = (_event: SyntheticEvent, inputValue: string, reason: string) => {
    if (reason === 'clear') {
      setKeyword('');
      return;
    }

    if (reason === 'input') {
      setKeyword(inputValue);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceOnSearch = useCallback(
    debounce((keyword: string) => {
      onSearch(keyword).then(setOptions);
    }, 600),
    [onSearch]
  );

  const handleRenderOption = (props: HTMLAttributes<HTMLLIElement>, option: UiOption) => {
    if (!onOptionRender) {
      const { label, value } = option;
      return (
        <Box component="li" {...props} key={value}>
          {label}
        </Box>
      );
    }

    return onOptionRender(props, option);
  };

  const handleOptionEqual = (option: UiOption, selected: UiOption) =>
    option?.value === selected.value;

  useEffect(() => {
    const safeKeyword = String(keyword).trim();

    if (!safeKeyword) {
      setOptions(sortedPropOptions);
      return;
    }

    debounceOnSearch(safeKeyword);
  }, [keyword, debounceOnSearch, sortedPropOptions]);

  const componentSize = size === 'x-small' ? 'small' : size;

  return (
    <FormControl label="" error={error} name={name} size={size} fullWidth>
      <Autocomplete
        className={`${styles.autocomplete} ${value.length ? styles.autocompleteWithSelections : ''}`}
        multiple
        options={options}
        filterOptions={(searchResultOptions) => searchResultOptions}
        getOptionLabel={({ label, labelSimple }) => labelSimple || (label as string)}
        onChange={handleChange}
        onInputChange={handleInputChange}
        inputValue={keyword}
        value={value}
        size={componentSize}
        fullWidth
        noOptionsText="&nbsp;"
        renderOption={handleRenderOption}
        isOptionEqualToValue={handleOptionEqual}
        ListboxProps={{
          style: { maxHeight: 300, overflow: 'auto' },
        }}
        renderInput={(params) => (
          <TextField {...params} hiddenLabel placeholder={label as string} variant="outlined" />
        )}
      />
    </FormControl>
  );
};

export default SelectSearchMulti;
